import { AxiosInstance } from 'axios';
import axiosClient from './axiosClientConfig';
import { HttpResponse } from 'interfaces/httpClient';
import {
	AppMetricsData,
	AverageTimeOnPage,
	CallToActionClicks,
	ClientMetricData,
	ClientMetricFilters,
	DataPointResponse,
	DeviceType,
	PageViews,
	Traffic,
	Visitors,
} from 'interfaces';

class LawyerMetricsService {
	constructor(private categories: AxiosInstance) {}

	async getClientMetrics(
		page: number,
		limit: number,
		activeUsers?: boolean,
		stateId?: number,
		date?: string,
		retention?: string,
		responseRate?: string,
		caseStatus?: string,
		segmentationId?: string[]
	): Promise<HttpResponse<ClientMetricData>> {
		const auxParams = [];
		activeUsers !== undefined && auxParams.push(`activeUsers=${activeUsers}`);
		stateId && auxParams.push(`stateId=${stateId}`);
		date && auxParams.push(`date=${date}`);
		retention && auxParams.push(`retention=${retention}`);
		responseRate && auxParams.push(`responseRate=${responseRate}`);
		caseStatus && auxParams.push(`caseStatus=${caseStatus}`);
		segmentationId &&
			segmentationId.forEach((id) => {
				auxParams.push('segmentationId=' + id);
			});
		const params = auxParams.join('&');
		return this.categories.get(
			params.length > 0
				? `/metrics/client?page=${page}&limit=${limit}&${params}`
				: `/metrics/client?page=${page}&limit=${limit}`
		);
	}
	async getAppMetrics(
		activeUsers?: boolean,
		stateId?: number,
		date?: string,
		retention?: string,
		responseRate?: string,
		caseStatus?: string,
		segmentationId?: number,
		graphFilter?: string
	): Promise<HttpResponse<AppMetricsData>> {
		const auxParams = [];
		activeUsers !== undefined && auxParams.push(`activeUsers=${activeUsers}`);
		stateId && auxParams.push(`stateId=${stateId}`);
		date && auxParams.push(`date=${date}`);
		retention && auxParams.push(`retention=${retention}`);
		graphFilter && auxParams.push(`graphFilter=${graphFilter}`);
		responseRate && auxParams.push(`responseRate=${responseRate}`);
		caseStatus && auxParams.push(`caseStatus=${caseStatus}`);
		segmentationId && auxParams.push(`segmentationId=${segmentationId}`);

		const params = auxParams.length > 0 ? `?${auxParams.join('&')}` : '';

		return this.categories.get(
			`/analytics/case-metrics/app-metrics-overview${params}`
		);
	}

	async getClientDatabaseExcel(
		page: number,
		limit?: number,
		activeUsers?: boolean,
		stateId?: number,
		date?: string,
		retention?: string,
		responseRate?: string,
		caseStatus?: string,
		segmentationId?: number
	): Promise<HttpResponse<ClientMetricData>> {
		const auxParams = [];
		limit && auxParams.push(`limit=${limit}`);
		activeUsers && auxParams.push(`activeUsers=${activeUsers}`);
		stateId && auxParams.push(`stateId=${stateId}`);
		date && auxParams.push(`date=${date}`);
		retention && auxParams.push(`retention=${retention}`);
		responseRate && auxParams.push(`responseRate=${responseRate}`);
		caseStatus && auxParams.push(`caseStatus=${caseStatus}`);
		segmentationId && auxParams.push(`segmentationId=${segmentationId}`);
		const params = auxParams.join('&');
		return this.categories.get(
			params.length > 0
				? `/metrics/metrics/excel?page=${page}&${params}`
				: `/metrics/metrics/excel?page=${page}`,
			{ responseType: 'arraybuffer' }
		);
	}

	async getClientMetricsFilters(
		page: number,
		limit?: number,
		activeUsers?: boolean,
		cityId?: number,
		date?: string,
		retention?: string,
		responseRate?: string,
		caseStatus?: string,
		segmentationId?: number
	): Promise<HttpResponse<ClientMetricFilters>> {
		const auxParams = [];
		limit && auxParams.push(`limit=${limit}`);
		activeUsers && auxParams.push(`activeUsers=${activeUsers}`);
		cityId && auxParams.push(`cityId=${cityId}`);
		date && auxParams.push(`date=${date}`);
		retention && auxParams.push(`retention=${retention}`);
		responseRate && auxParams.push(`responseRate=${responseRate}`);
		caseStatus && auxParams.push(`caseStatus=${caseStatus}`);
		segmentationId && auxParams.push(`segmentationId=${segmentationId}`);
		const params = auxParams.join('&');
		return this.categories.get(
			params.length > 0
				? `/metrics/client/filters?page=${page}&${params}`
				: `/metrics/client/filters?page=${page}`
		);
	}

	// ----------------- Google Analytics ----------------//
	async getVisitors(date?: string): Promise<HttpResponse<Visitors>> {
		return this.categories.get(
			date
				? `/google-analitics/visitors?date=${date}`
				: `/google-analitics/visitors`
		);
	}
	async getTotalVisitorsMonth(
		date?: string
	): Promise<HttpResponse<DataPointResponse[]>> {
		return this.categories.get(
			date
				? `/google-analitics/total-visitors-month?date=${date}`
				: `/google-analitics/total-visitors-month`
		);
	}
	async getTraffic(date?: string): Promise<HttpResponse<Traffic>> {
		return this.categories.get(
			date
				? `/google-analitics/traffic?date=${date}`
				: `/google-analitics/traffic`
		);
	}
	async getPageViews(date?: string): Promise<HttpResponse<PageViews>> {
		return this.categories.get(
			date
				? `/google-analitics/page-views?date=${date}`
				: `/google-analitics/page-views`
		);
	}
	async getTotalPageViews(
		date?: string
	): Promise<HttpResponse<DataPointResponse[]>> {
		return this.categories.get(
			date
				? `/google-analitics/page-views-month?date=${date}`
				: `/google-analitics/page-views-month`
		);
	}
	async getDeviceType(date?: string): Promise<HttpResponse<DeviceType>> {
		return this.categories.get(
			date
				? `/google-analitics/device-type?date=${date}`
				: `/google-analitics/device-type`
		);
	}
	async getAverageTimeOnPage(
		date?: string
	): Promise<HttpResponse<AverageTimeOnPage>> {
		return this.categories.get(
			date
				? `/google-analitics/average-time-on-page?date=${date}`
				: `/google-analitics/average-time-on-page`
		);
	}
	async getAverageTimeOnPageMonth(
		date?: string
	): Promise<HttpResponse<DataPointResponse[]>> {
		return this.categories.get(
			date
				? `/google-analitics/average-time-on-page-month?date=${date}`
				: `/google-analitics/average-time-on-page-month`
		);
	}
	async getCallToActionClicks(
		date?: string
	): Promise<HttpResponse<CallToActionClicks>> {
		return this.categories.get(
			date
				? `/google-analitics/call-to-action-clicks?date=${date}`
				: `/google-analitics/call-to-action-clicks`
		);
	}

	async getDevicesRegistered(date?: string) {
		return this.categories.get(
			date
				? `/google-analitics/client-app-device-type?date=${date}`
				: `/google-analitics/client-app-device-type`
		);
	}
}

export const lawyerMetricsService = new LawyerMetricsService(axiosClient);
